/**
 * A proposed structure, flattened into rows you can draw as a branch.
 *
 * WHAT THIS REPLACED. The panel drew a box per category with a nested list of
 * topics inside it — a shape that could only ever show two levels, because that
 * is all the model had. The model has none of that limit now, so the proposal
 * is drawn the way the rail draws the real tree: one indented list, depth
 * carrying the structure, no boxes.
 *
 * Its own dependency-free module because the panel beside it imports React and
 * the router, and the walk here is the part that can quietly be wrong — a
 * mis-computed depth silently reparents a whole branch on screen.
 */
import type { StructurePlanNode, StructurePlanTree } from '../types';

export interface PlanRow {
  node: StructurePlanNode;
  depth: number;
  /** `A / B / C` — what the agent's tools address a node by. */
  path: string;
  /**
   * `['categories', 0, 'topics', 2]` — how the DELETE endpoint addresses it.
   * Built here rather than at the call site so a row at any depth can be
   * removed; the endpoint already walked a generic path, the renderer just
   * never had one deeper than two.
   */
  indexPath: (string | number)[];
  /** True for the last child of its parent, so the branch can close its line. */
  isLast: boolean;
  hasChildren: boolean;
  seedCount: number;
}

export function planRows(tree: StructurePlanTree | null): PlanRow[] {
  if (!tree) return [];
  const rows: PlanRow[] = [];

  const walk = (
    nodes: StructurePlanNode[] | undefined,
    depth: number,
    prefix: string,
    guard: Set<StructurePlanNode>,
    indexPrefix: (string | number)[],
  ) => {
    const list = (nodes ?? []).filter((n) => n && typeof n.name === 'string');
    list.forEach((node, i) => {
      // A tree that referenced itself would otherwise recurse forever. It comes
      // from an LLM via JSON, so this is cheap insurance rather than paranoia.
      if (guard.has(node)) return;
      const children = (node.topics ?? []).filter(Boolean);
      const path = prefix ? `${prefix} / ${node.name}` : node.name;
      // The index is into the ORIGINAL array, not the filtered one — the
      // endpoint indexes the stored tree, which still holds the malformed
      // entries this walk skips.
      const indexPath = [...indexPrefix, (nodes ?? []).indexOf(node)];
      rows.push({
        node,
        depth,
        path,
        indexPath,
        isLast: i === list.length - 1,
        hasChildren: children.length > 0,
        seedCount: (node.seed_keywords ?? []).length,
      });
      walk(children, depth + 1, path, new Set(guard).add(node), [...indexPath, 'topics']);
    });
  };

  walk(tree.categories, 0, '', new Set(), ['categories']);
  return rows;
}

/** Nodes, and seeds, at any depth — the counts the panel reports. */
export function planTotals(tree: StructurePlanTree | null): {
  nodes: number;
  seeds: number;
  depth: number;
} {
  const rows = planRows(tree);
  return {
    nodes: rows.length,
    seeds: rows.reduce((n, r) => n + r.seedCount, 0),
    // 0 for an empty tree; 1 for a flat list of roots.
    depth: rows.reduce((d, r) => Math.max(d, r.depth + 1), 0),
  };
}
